import axios, { AxiosInstance, AxiosResponse } from 'axios';

export type MetricsLeadStatus = 'NEW' | 'CONTACTED' | 'REACTIVATED' | 'DISQUALIFIED';

export interface MetricsLead {
  id: string;
  cnpj: string;
  channel?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  customerName?: string;
  phone?: string;
  email?: string;
  notes?: string;
  status: MetricsLeadStatus;
  loyaltyCustomerId?: string;
  lastInteractionAt?: string;
  lastCampaignTriggeredAt?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
}

export interface ListMetricsLeadsParams {
  status?: MetricsLeadStatus;
  channel?: string;
  loyaltyCustomerId?: string;
  searchTerm?: string;
  createdFrom?: string;
  createdTo?: string;
  cnpj?: string;
}

export interface UpdateMetricsLeadPayload {
  status: MetricsLeadStatus;
  notes?: string;
  metadata?: Record<string, any>;
  customerName?: string;
  phone?: string;
  email?: string;
  loyaltyCustomerId?: string;
}

export interface ReactivateLeadPayload {
  referenceDate?: string;
}

class MetricsLeadService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 15000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  async listLeads(params?: ListMetricsLeadsParams): Promise<MetricsLead[]> {
    try {
      const response: AxiosResponse<MetricsLead[]> = await this.client.get('/admin/metrics/leads', {
        params: {
          ...params,
          createdFrom: params?.createdFrom || undefined,
          createdTo: params?.createdTo || undefined,
          channel: params?.channel?.trim() || undefined,
          searchTerm: params?.searchTerm?.trim() || undefined,
          loyaltyCustomerId: params?.loyaltyCustomerId?.trim() || undefined,
          cnpj: params?.cnpj?.replace(/\D/g, '') || undefined,
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar leads');
    }
  }

  async updateLeadStatus(id: string, payload: UpdateMetricsLeadPayload & { cnpj?: string }): Promise<MetricsLead> {
    try {
      const response: AxiosResponse<MetricsLead> = await this.client.put(
        `/admin/metrics/leads/${id}/status`,
        {
          status: payload.status,
          notes: payload.notes,
          metadata: payload.metadata,
          customerName: payload.customerName,
          phone: payload.phone,
          email: payload.email,
          loyaltyCustomerId: payload.loyaltyCustomerId,
        },
        {
          params: payload.cnpj
            ? {
                cnpj: payload.cnpj.replace(/\D/g, ''),
              }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar lead');
    }
  }

  async reactivateLead(id: string, payload: ReactivateLeadPayload & { cnpj?: string }): Promise<MetricsLead> {
    try {
      const response: AxiosResponse<MetricsLead> = await this.client.post(
        `/admin/metrics/leads/${id}/reactivate`,
        {
          referenceDate: payload.referenceDate,
        },
        {
          params: payload.cnpj
            ? {
                cnpj: payload.cnpj.replace(/\D/g, ''),
              }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao reativar lead');
    }
  }
}

export const metricsLeadService = new MetricsLeadService();
export default metricsLeadService;
